import { useCallback, useState } from "react";
import type { ReactElement } from "react";
import type { ModuleId, ModuleRecord } from "../types";
import { usePwaInstall } from "../hooks/usePwaInstall";
import { InstallHelpIos } from "./InstallHelpIos";

interface ModuleHomeProps {
  modules: ModuleRecord[];
  onSelectModule: (moduleId: ModuleId) => void;
}

// SVGs custom por modulo. Estetica linear, stroke 1.6, viewBox 24x24, sem fill,
// usando currentColor para herdar a cor do card (cyan da paleta da landing).
const MODULE_ICONS: Record<ModuleId, ReactElement> = {
  // Mod 1 - Via aerea e IOT: tubo orotraqueal + balonete
  "modulo-01": (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M5 8h7l4 4-4 4H5z" />
      <path d="M12 8v8" />
      <circle cx="18.5" cy="12" r="2.2" />
      <path d="M3 12h2" />
    </svg>
  ),
  // Mod 2 - Pos-intubacao e confirmacao: traqueia + check
  "modulo-02": (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M9 3v6c0 1.5-1 2.5-1 4s1 2.5 1 4v4" />
      <path d="M13 3v6c0 1.5 1 2.5 1 4s-1 2.5-1 4v4" />
      <path d="M9 7h4M9 11h4M9 15h4M9 19h4" />
      <path d="M17 12l1.6 1.6L21 11" />
    </svg>
  ),
  // Mod 3 - Ventilacao mecanica: pulmoes + curva
  "modulo-03": (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 4v8" />
      <path d="M12 6c-1.5-2-4-2-5.5-.5C5 7 5 9.5 6 12c1 2.5 2 4 4 5 1 .5 2 0 2-1V6z" />
      <path d="M12 6c1.5-2 4-2 5.5-.5 1.5 1.5 1.5 4 .5 6.5-1 2.5-2 4-4 5-1 .5-2 0-2-1V6z" />
      <path d="M3 19c1.5-1 2.5-1 4 0s2.5 1 4 0 2.5-1 4 0 2.5 1 4 0" />
    </svg>
  ),
  // Mod 4 - Sedoanalgesia: capsula + onda
  "modulo-04": (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <rect x="3" y="9" width="14" height="6" rx="3" />
      <path d="M10 9v6" />
      <path d="M17 12h1l1.5-1.5L21 13l-1.5 1.5L21 16" />
    </svg>
  ),
  // Mod 5 - Drogas vasoativas: coracao + pulso
  "modulo-05": (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M3 12h3l2-4 3 8 2-5 1.5 3H21" />
      <path d="M12 19c-2-1.5-5-3.5-6.5-6C4 10.5 5 7 8 7c1.5 0 3 1 4 2.5C13 8 14.5 7 16 7c3 0 4 3.5 2.5 6-.6 1-1.6 2-2.7 2.8" />
    </svg>
  ),
  // Mod 6 - Disturbios hidroeletroliticos: gota + Na+
  "modulo-06": (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 3c-2 4-5 7-5 11a5 5 0 0010 0c0-4-3-7-5-11z" />
      <path d="M9 13h6M12 10v6" />
    </svg>
  ),
  // Mod 7 - Antibioticos/Sepse: frasco + escudo
  "modulo-07": (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M9 3h6l1 5H8z" />
      <rect x="7" y="8" width="10" height="12" rx="2" />
      <path d="M10 13h4M12 11v4" />
    </svg>
  ),
  // Mod 8 - Febre em UTI: termômetro
  "modulo-08": (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M14 14.76V5a2 2 0 0 0-4 0v9.76a4 4 0 1 0 4 0z" />
      <path d="M12 8v8" />
      <circle cx="12" cy="18" r="1.4" fill="currentColor" stroke="none" />
    </svg>
  ),
  // Mod 9 - Neutropenia febril: escudo (defesa imune) + cruz
  "modulo-09": (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 3l7 3v5c0 4.5-3 8-7 9-4-1-7-4.5-7-9V6l7-3z" />
      <path d="M12 9.5v5M9.5 12h5" />
    </svg>
  ),
  // Mod 10 - Neuroemergencias: cerebro + raio
  "modulo-10": (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M9.5 3C6.46 3 4 5.46 4 8.5c0 2.18 1.23 4.06 3 5.02V19h10v-5.48C18.77 12.56 20 10.68 20 8.5 20 5.46 17.54 3 14.5 3H9.5z" />
      <path d="M13 8l-2.5 3.5H13L10.5 16" />
    </svg>
  ),
  // Mod 11 - SCA/IAM: coracao com ECG
  "modulo-11": (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 21C12 21 4 15 4 9a4 4 0 018 0 4 4 0 018 0c0 6-8 12-8 12z" />
      <path d="M2 12h3l2-3 2 6 2-5 1.5 2H22" />
    </svg>
  ),
  // Mod 12 - TEP: pulmões + trombo
  "modulo-12": (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 4v7" />
      <path d="M12 7c-1.2-1.6-3.2-1.6-4.4-.4C6 8 6 10.5 6.4 13c.4 2.4 1.2 4 2.8 4.6 1 .4 2-.2 2-1.2V7z" />
      <path d="M12 7c1.2-1.6 3.2-1.6 4.4-.4C17.6 8 17.6 10.5 17.2 13c-.4 2.4-1.2 4-2.8 4.6-1 .4-2-.2-2-1.2V7z" />
      <circle cx="15" cy="12" r="1.4" fill="currentColor" stroke="none" />
    </svg>
  ),
  // Mod 13 - CAD/EHH: gota (glicemia) + cruz metabólica
  // Mod 14 - Perfusao e hemodinamica: onda de pulso caindo + gota de perfusao
  "modulo-14": (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M2 14h3l2-5 3 9 2.5-6 1.5 3h3" />
      <circle cx="19.5" cy="7" r="2.3" />
      <path d="M19.5 4.7V7l1.6 1.1" />
    </svg>
  ),
  "modulo-13": (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 3c-2.5 4-5 6.8-5 10a5 5 0 0 0 10 0c0-3.2-2.5-6-5-10z" />
      <path d="M10 13h4M12 11v4" />
    </svg>
  ),
  // Acessos e procedimentos: agulha entrando num vaso (duas paredes paralelas)
  "modulo-15": (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M2.5 14.5h19" />
      <path d="M2.5 19.5h19" />
      <path d="M7.5 3.5L14 16.5" />
      <path d="M5.8 4.8l3.4-1.7" />
    </svg>
  ),
  // Calculadoras (cauda, sem número): calculadora classica
  calculadoras: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <rect x="5" y="3" width="14" height="18" rx="2.5" />
      <rect x="7.5" y="5.5" width="9" height="3.5" rx="1" />
      <circle cx="9" cy="13" r="0.9" fill="currentColor" stroke="none" />
      <circle cx="12" cy="13" r="0.9" fill="currentColor" stroke="none" />
      <circle cx="15" cy="13" r="0.9" fill="currentColor" stroke="none" />
      <circle cx="9" cy="17" r="0.9" fill="currentColor" stroke="none" />
      <circle cx="12" cy="17" r="0.9" fill="currentColor" stroke="none" />
      <circle cx="15" cy="17" r="0.9" fill="currentColor" stroke="none" />
    </svg>
  ),
  // Mod 15 - Emergencias pediatricas: coracao (emergencia) com cruz medica
  pediatria: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 20.5s-6-3.7-8-8A4.3 4.3 0 0 1 12 6.2 4.3 4.3 0 0 1 20 8.5c-2 4.3-8 8-8 8z" />
      <path d="M12 9.8v3.6M10.2 11.6h3.6" />
    </svg>
  ),
  // Mod 16 - Caderno de questoes: prancheta com checks
  caderno: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <rect x="5" y="4" width="14" height="17" rx="2" />
      <path d="M9 4v-1h6v1" />
      <path d="M8.5 10l1.5 1.5L13 8" />
      <path d="M8.5 15l1.5 1.5L13 13" />
      <path d="M15 10h2" />
      <path d="M15 15h2" />
    </svg>
  ),
  // Mod 17 - Referencias: livro aberto
  referencias: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M3 5c2.5-1 5.5-1 9 1v13c-3.5-2-6.5-2-9-1z" />
      <path d="M21 5c-2.5-1-5.5-1-9 1v13c3.5-2 6.5-2 9-1z" />
      <path d="M12 6v13" />
    </svg>
  )
};

// Identifica visualmente a familia do modulo para colorir a tarja lateral.
function familyOf(moduleId: ModuleId): "clinico" | "calc" | "quiz" | "ref" {
  if (moduleId === "calculadoras") return "calc";
  if (moduleId === "caderno") return "quiz";
  if (moduleId === "referencias") return "ref";
  return "clinico"; // clínicos 1…N + pediatria
}

export function ModuleHome({ modules, onSelectModule }: ModuleHomeProps) {
  const { isIos, isStandalone, canInstallNatively, promptInstall } = usePwaInstall();
  const [iosHelpOpen, setIosHelpOpen] = useState(false);

  // Hero de instalação só faz sentido quando ainda não está instalado e há um
  // caminho acionável: prompt nativo (Android/desktop) ou tutorial manual (iOS).
  const showInstall = !isStandalone && (canInstallNatively || isIos);

  const handleInstall = useCallback(() => {
    void promptInstall();
  }, [promptInstall]);

  return (
    <section className="module-home" aria-label="Página inicial do manual">
      <div className="module-home__hero">
        <div className="module-home__hero-inner">
          <img
            src="/virtus-logo/virtus_logo_horizontal_transparente.png"
            alt="Virtus Clinical Tools"
            className="module-home__logo"
          />
          <span className="module-home__eyebrow">Manual de Medicina Intensiva</span>
          <h1 className="module-home__title">
            Decisões à beira do leito,
            <br />
            com a precisão da literatura.
          </h1>
          <p className="module-home__lead">
            Ferramentas práticas de cabeceira para o cenário de cuidados intensivos —
            protocolos clínicos e calculadoras operacionais para tomada de decisão.
          </p>

          <button
            type="button"
            className="module-home__cta"
            onClick={() => onSelectModule(modules[0].id)}
          >
            Começar pelo Módulo 1
            <span aria-hidden="true">→</span>
          </button>

          <p className="module-home__disclaimer">
            Material educacional de apoio à decisão clínica. Confirme doses e condutas
            no protocolo do seu serviço e na bula vigente antes de aplicar.
          </p>
        </div>
      </div>

      <div className="module-home__grid-wrap">
        {showInstall ? (
          <div className="module-home__install">
            <span className="module-home__install-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 4v10" />
                <path d="M8 10l4 4 4-4" />
                <path d="M4 18h16" />
              </svg>
            </span>
            <div className="module-home__install-copy">
              <strong className="module-home__install-title">
                Tenha o Manual sempre à mão
              </strong>
              <span className="module-home__install-lead">
                {isIos
                  ? "Adicione à tela de início do iPhone/iPad e abra como app — em tela cheia e disponível offline."
                  : "Instale como app: abre em janela própria, sem barra do navegador, e funciona offline."}
              </span>
            </div>
            <button
              type="button"
              className="module-home__install-cta"
              onClick={isIos ? () => setIosHelpOpen(true) : handleInstall}
            >
              {isIos ? "Como instalar" : "Instalar app"}
            </button>
          </div>
        ) : null}

        <div className="module-home__grid-header">
          <span className="module-home__grid-eyebrow">Sumário</span>
          <h2 className="module-home__grid-title">Acesso direto aos módulos</h2>
        </div>

        <div className="module-home__list" role="list">
          {modules.map((module) => {
            const family = familyOf(module.id);
            return (
              <button
                key={module.id}
                type="button"
                role="listitem"
                className={`module-home__row module-home__row--${family}`}
                onClick={() => onSelectModule(module.id)}
                aria-label={module.number === null ? `Abrir ${module.title}` : `Abrir Módulo ${module.number}: ${module.title}`}
              >
                <span className="module-home__row-num">{module.number === null ? "·" : String(module.number).padStart(2, "0")}</span>
                <span className="module-home__row-icon" aria-hidden="true">
                  {MODULE_ICONS[module.id]}
                </span>
                <span className="module-home__row-title">{module.title}</span>
                <span className="module-home__row-chevron" aria-hidden="true">›</span>
              </button>
            );
          })}
        </div>
      </div>

      {iosHelpOpen ? <InstallHelpIos onClose={() => setIosHelpOpen(false)} /> : null}
    </section>
  );
}
